import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, MailOpen, Mail, Trash2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { useLanguage } from "@/context/AppContext";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

function formatDetail(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

async function authFetch(config, retry = true) {
  try {
    return await api(config);
  } catch (e) {
    if (e.response?.status === 401 && retry) {
      await api.post("/auth/refresh");
      return authFetch(config, false);
    }
    throw e;
  }
}

export default function Admin() {
  const { lang } = useLanguage();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("");

  const loadEnquiries = useCallback(async () => {
    const { data } = await authFetch({ url: "/admin/enquiries" });
    setEnquiries(data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authFetch({ url: "/auth/me" });
        setUser(data);
        await loadEnquiries();
      } catch {
        setUser(null);
      } finally {
        setChecking(false);
      }
    })();
  }, [loadEnquiries]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      await loadEnquiries();
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    setEnquiries([]);
  };

  const toggleRead = async (id) => {
    const { data } = await authFetch({ url: `/admin/enquiries/${id}/read`, method: "PATCH" });
    setEnquiries((list) => list.map((en) => (en.id === id ? data : en)));
  };

  const remove = async (id) => {
    await authFetch({ url: `/admin/enquiries/${id}`, method: "DELETE" });
    setEnquiries((list) => list.filter((en) => en.id !== id));
    toast.success("Enquiry deleted");
  };

  const filtered = enquiries.filter((en) =>
    [en.name, en.email, en.subject, en.message].join(" ").toLowerCase().includes(filter.toLowerCase())
  );
  const unread = enquiries.filter((en) => !en.read).length;

  const inputCls =
    "w-full border border-gold/15 bg-ink/70 px-4 py-3 text-sm text-cream placeholder:text-fern outline-none transition-colors duration-300 focus:border-gold/60";

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading">
        <span className="font-mono text-xs uppercase tracking-[0.35em] text-gold">My10Sons</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-4" data-testid="admin-login-view">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-sm border border-gold/15 bg-moss/40 p-8">
          <div className="mb-8 flex items-center gap-3">
            <LogoMark size={34} />
            <div>
              <div className="font-display text-xl text-cream">Family Office</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fern">My10Sons Admin</div>
            </div>
          </div>
          <label className="block">
            <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-gold-light">Email</span>
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} dir="ltr" />
          </label>
          <label className="mt-5 block">
            <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-gold-light">Password</span>
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} dir="ltr" />
          </label>
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-red-400">{error}</p>
          )}
          <button data-testid="admin-login-submit-button" type="submit" className="mt-8 w-full rounded-full bg-gradient-to-br from-gold-light via-gold to-gold-deep px-7 py-3.5 text-sm font-semibold text-ink transition-transform duration-300 hover:scale-[1.02]">
            Sign in
          </button>
          <Link to="/" data-testid="admin-back-link" className="mt-5 block text-center text-xs text-fern transition-colors hover:text-gold-light">
            Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-cream" data-testid="admin-dashboard">
      <header className="border-b border-gold/10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-5 sm:px-8">
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <div>
              <div className="font-display text-lg">Family Office</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fern">
                {enquiries.length} enquiries · {unread} unread
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-to-site" className="text-xs text-fern transition-colors hover:text-gold-light">
              View site
            </Link>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 rounded-full border border-gold/25 px-4 py-2 text-xs text-gold-light transition-colors hover:bg-gold/10">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        <input
          data-testid="admin-enquiry-filter-input"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search enquiries…"
          className={`${inputCls} mb-8 max-w-md`}
        />

        <div data-testid="admin-enquiry-list" className="space-y-4">
          {filtered.length === 0 && (
            <p data-testid="admin-enquiry-empty" className="py-16 text-center text-sm text-fern">No enquiries yet.</p>
          )}
          {filtered.map((en) => (
            <article
              key={en.id}
              data-testid={`admin-enquiry-item-${en.id}`}
              className={`border p-6 transition-colors duration-300 ${en.read ? "border-gold/10 bg-moss/30 opacity-70" : "border-gold/30 bg-moss/50"}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-display text-xl text-cream">{en.name}</h3>
                    {!en.read && <span className="rounded-full bg-gold/15 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-gold-light">New</span>}
                  </div>
                  <div className="mt-1 font-mono text-xs text-fern" dir="ltr">{en.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full border border-gold/20 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold-light">{en.subject}</span>
                  <button data-testid={`admin-enquiry-read-${en.id}`} onClick={() => toggleRead(en.id)} className="rounded-full border border-gold/20 p-2 text-sage transition-colors hover:border-gold/60 hover:text-gold-light" aria-label="Toggle read">
                    {en.read ? <Mail size={14} /> : <MailOpen size={14} />}
                  </button>
                  <button data-testid={`admin-enquiry-delete-${en.id}`} onClick={() => remove(en.id)} className="rounded-full border border-gold/20 p-2 text-sage transition-colors hover:border-red-400/60 hover:text-red-400" aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <p className="mt-4 text-sm font-light leading-relaxed text-sage">{en.message}</p>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-fern">
                {new Date(en.created_at).toLocaleString(lang === "ar" ? "ar" : "en-GB")}
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
